import { useCustomCss } from '../lib/customCss';
import { Copy, Crown, LogOut, Pencil, UserPlus, Users, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, tokens } from '../lib/api';
import type { FamilyGroup, Parent, PermissionCatalog, PermissionRole } from '../lib/types';
import { Collapsible, Fold } from '../ui/Collapsible';
import { Logo } from '../ui/Logo';
import { useParent } from './ParentContext';
import { MemberPermissionsEditor, roleLabel } from './MemberPermissions';
import { IncomingMerge, RequestMerge, type MergeRequests } from './MergeFamilies';


/** Settings card: who shares each of your families, and invites to add more. */
export function FamilySettings() {
  const { toast, setParent } = useParent();
  const [families, setFamilies] = useState<FamilyGroup[] | null>(null);
  const [catalog, setCatalog] = useState<PermissionCatalog | undefined>();
  // Requests to combine families (#509): ones waiting for you, and ones you sent.
  const [merges, setMerges] = useState<MergeRequests>({ incoming: [], outgoing: [] });

  const load = useCallback(() => {
    api.get<MergeRequests>('/api/family/merge-requests').then(setMerges).catch(() => undefined);
    api
      .get<{ families: FamilyGroup[]; permissions?: PermissionCatalog }>('/api/family')
      .then((r) => {
        setFamilies(r.families);
        setCatalog(r.permissions);
      })
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  useEffect(load, [load]);

  if (!families) return null;

  return (
    <Collapsible id="family" title="Family and Sharing" icon={<Users size={18} />}>
      <p className="muted small">
        Invite another parent or guardian to share a family. Everyone you add sees the family’s children (you choose which ones)
        and can do what you allow under “Can do”: everything as a co-parent, or less as a tutor, viewer or custom role.
      </p>
      {merges.incoming.map((r) => (
        <IncomingMerge key={r.id} request={r} onDone={load} />
      ))}
      {families.map((f) => (
        <FamilyCard key={f.id} family={f} catalog={catalog} reload={load} outgoing={merges.outgoing} />
      ))}
      {/* Someone who only helps in other people's families can start their own (#507). */}
      {!families.some((f) => f.members.some((m) => m.isYou && m.role === 'owner')) && (
        <div className="family-start">
          <p className="muted small">You’re helping in {families.length ? 'someone else’s family' : 'no family yet'}. Have children of your own to add?</p>
          <button
            className="btn"
            onClick={() =>
              api
                .post<Parent>('/api/family')
                .then((p) => {
                  // A new family that has to choose its plan first (#635, #638): open the plans page.
                  if (p.billing?.locked) {
                    window.location.assign('/');
                    return;
                  }
                  setParent(p);
                  load();
                  toast({ text: 'Your family is ready. Add a child from Home.', tone: 'good' });
                })
                .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }))
            }
          >
            <UserPlus size={15} /> Start your own family
          </button>
        </div>
      )}
    </Collapsible>
  );
}

function FamilyCard({
  family,
  catalog,
  reload,
  outgoing,
}: {
  family: FamilyGroup;
  catalog: PermissionCatalog | undefined;
  reload: () => void;
  outgoing: MergeRequests['outgoing'];
}) {
  const { toast } = useParent();
  const [email, setEmail] = useState('');
  // What the invited adult may do once they join (#493): Co-parent, Tutor or Viewer. Custom is set after they join.
  const [role, setRole] = useState<PermissionRole>('coparent');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<{ url: string; emailed: boolean; email: string } | null>(null);
  /** Only the family's owner can add or remove people; members see the roster read-only. */
  const iAmOwner = family.members.some((m) => m.isYou && m.role === 'owner');
  // Renaming the family (#494): the owner edits the name in place.
  const [naming, setNaming] = useState<string | null>(null);
  async function rename() {
    if (naming === null) return;
    await act(() => api.put(`/api/family/${family.id}/name`, { name: naming.trim() }), 'Family renamed.');
    setNaming(null);
  }

  async function act(run: () => Promise<unknown>, done: string) {
    try {
      await run();
      if (done) toast({ text: done, tone: 'good' });
      reload();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  async function invite() {
    setBusy(true);
    try {
      const r = await api.post<{ url: string; emailed: boolean }>(`/api/family/${family.id}/invite`, { email: email.trim(), role });
      setLink({ ...r, email: email.trim() });
      setEmail('');
      reload();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  // Each family is a fold in Family and Sharing (#601), titled with its name; the fold gives it its border.
  const people = family.members.length;
  const kids = family.children.length;
  const summary = iAmOwner
    ? `You own it · ${people} member${people === 1 ? '' : 's'}${family.invites.length ? ` · ${family.invites.length} invited` : ''}`
    : `Member · ${kids} child${kids === 1 ? '' : 'ren'}`;
  return (
    <Fold title={family.name} summary={summary}>
      <div className="family-card-body">
        {naming !== null ? (
          <div className="row-between family-rename">
            <input
              className="grow"
              value={naming}
              maxLength={60}
              autoFocus
              onChange={(e) => setNaming(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void rename();
                if (e.key === 'Escape') setNaming(null);
              }}
              aria-label="Family name"
            />
            <button className="btn small primary" onClick={() => void rename()} disabled={!naming.trim() || naming.trim() === family.name}>
              Save
            </button>
            <button className="btn small" onClick={() => setNaming(null)}>
              Cancel
            </button>
          </div>
        ) : (
          <div className="family-card-head">
            {family.isHome ? <span className="tag">Your family</span> : <span className="tag feature">Shared with you</span>}
            {iAmOwner && (
              <button className="btn small" onClick={() => setNaming(family.name)} aria-label={`Rename ${family.name}`}>
                <Pencil size={14} /> Rename
              </button>
            )}
          </div>
        )}
        <ul className="family-members">
          {family.members.map((m) => {
            const canManageAccess = iAmOwner && !m.isYou && m.role !== 'owner';
            const toggleAccess = (childId: string, grant: boolean) => {
              const next = grant ? [...new Set([...m.childIds, childId])] : m.childIds.filter((id) => id !== childId);
              act(() => api.put(`/api/family/${family.id}/members/${m.id}/access`, { childIds: next }), '');
            };
            return (
              <li key={m.id} className="family-member">
                <div className="row-between">
                  <span className="grow">
                    {m.name}
                    {m.role === 'owner' && (
                      <span title="Owner">
                        <Crown size={13} className="owner-icon" />
                      </span>
                    )}
                    <span className="muted small"> · {m.email}</span>
                    {m.isYou && <span className="muted small"> · you</span>}
                  </span>
                  {canManageAccess && (
                    <button
                      className="btn small"
                      onClick={() =>
                        confirm(
                          `Make ${m.name} the owner of ${family.name}? They'll manage who's in it and its plan; you'll stay in it as a Co-parent.`,
                        ) && act(() => api.put(`/api/family/${family.id}/owner`, { parentId: m.id }), `${m.name} now owns ${family.name}.`)
                      }
                    >
                      <Crown size={14} /> Make owner
                    </button>
                  )}
                  {canManageAccess && (
                    <button
                      className="icon-btn"
                      aria-label={`Remove ${m.name}`}
                      onClick={() => confirm(`Remove ${m.name} from ${family.name}?`) && act(() => api.post(`/api/family/${family.id}/remove`, { parentId: m.id }), `Removed ${m.name}.`)}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                {canManageAccess && !!family.children.length && (
                  <div className="member-access">
                    <span className="muted small">Can see:</span>
                    {family.children.map((c) => (
                      <label key={c.id} className="access-chip">
                        <input type="checkbox" checked={m.childIds.includes(c.id)} onChange={(e) => toggleAccess(c.id, e.target.checked)} />
                        <span>{c.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {canManageAccess && catalog && <MemberPermissionsEditor familyId={family.id} member={m} catalog={catalog} onSaved={reload} />}
                {!canManageAccess && m.role !== 'owner' && m.permissions && (
                  <div className="member-access muted small">Can do: {roleLabel(catalog, m.permissions.role)}</div>
                )}
                {!canManageAccess && m.role !== 'owner' && !!family.children.length && (
                  <div className="member-access muted small">
                    Sees: {family.children.filter((c) => m.childIds.includes(c.id)).map((c) => c.name).join(', ') || 'no students'}
                  </div>
                )}
              </li>
            );
          })}
          {family.invites.map((i) => (
            <li key={i.email} className="pending">
              <span className="grow">
                {i.email} <span className="muted small">· invited as {roleLabel(catalog, i.role).toLowerCase()}</span>
              </span>
              {iAmOwner && (
                <button className="btn small" onClick={() => act(() => api.post(`/api/family/${family.id}/invite/cancel`, { email: i.email }), 'Invite cancelled.')}>
                  Cancel
                </button>
              )}
            </li>
          ))}
        </ul>

        {!iAmOwner ? (
          <p className="muted small">Only the owner of {family.name} can add or remove people.</p>
        ) : link ? (
          <div className="notice stack link-result">
            <div className="small">
              {link.emailed ? `Emailed an invite to ${link.email}. ` : `Email isn’t set up, so send this invite link to ${link.email} yourself. `}
              It works for 7 days.
            </div>
            <div className="row-between">
              <input className="grow mono small" readOnly value={link.url} onFocus={(e) => e.target.select()} aria-label="Invite link" />
              <button className="btn small" onClick={() => navigator.clipboard?.writeText(link.url).then(() => toast({ text: 'Link copied.', tone: 'good' }))}>
                <Copy size={15} /> Copy
              </button>
            </div>
            <div>
              <button className="btn small" onClick={() => setLink(null)}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="row-between family-invite-row">
            <input className="grow" type="email" placeholder="parent@example.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-label={`Invite to ${family.name}`} />
            <select className="relationship-select" value={role} onChange={(e) => setRole(e.target.value as PermissionRole)} aria-label="They can do">
              {(catalog?.roles ?? [{ value: 'coparent' as const, label: 'Co-parent', hint: '' }])
                .filter((r) => r.value !== 'custom')
                .map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
            </select>
            <button className="btn small primary" onClick={invite} disabled={busy || !email.includes('@')}>
              <UserPlus size={15} /> Invite
            </button>
          </div>
        )}
        {iAmOwner && <RequestMerge familyId={family.id} outgoing={outgoing} onChange={reload} />}

        {!family.isHome && (
          <div>
            <button
              className="btn small danger-text"
              onClick={() => confirm(`Leave ${family.name}? You’ll lose access to its children.`) && act(() => api.post(`/api/family/${family.id}/leave`), `You left ${family.name}.`)}
            >
              <LogOut size={15} /> Leave this family
            </button>
          </div>
        )}
      </div>
    </Fold>
  );
}

/** The page an invited parent lands on from their email link (works signed in or out). */
export function FamilyInviteAccept() {
  useCustomCss('login'); // an admin's custom styles (#367)
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [info, setInfo] = useState<{ familyName: string; invitedBy: string; email: string; hasAccount: boolean; roleLabel?: string } | null>(null);
  const [signedIn, setSignedIn] = useState<Parent | null>(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ familyName: string; invitedBy: string; email: string; hasAccount: boolean; roleLabel?: string }>(`/api/family/invite/${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch((e) => setError(e.message));
    if (tokens.parent()) api.get<Parent>('/api/me').then(setSignedIn).catch(() => setSignedIn(null));
  }, [token]);

  async function accept() {
    setBusy(true);
    try {
      await api.post(`/api/family/invite/${encodeURIComponent(token)}/accept`);
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const wrongAccount = signedIn && info && signedIn.email.toLowerCase() !== info.email.toLowerCase();

  return (
    <main className="auth">
      <div className="auth-card card">
        <Logo large />
        <h1>Family invite</h1>
        {error ? (
          <p className="error">{error}</p>
        ) : !info ? (
          <p className="muted">One moment…</p>
        ) : done ? (
          <>
            <p className="good-text">You’ve joined {info.familyName}.</p>
            <Link to="/" className="btn primary big">
              Open ClassGuardian
            </Link>
          </>
        ) : (
          <>
            <p>
              <strong>{info.invitedBy}</strong> invited <strong>{info.email}</strong> to help watch over <strong>{info.familyName}</strong> on
              ClassGuardian, as a <strong>{(info.roleLabel ?? 'Co-parent').toLowerCase()}</strong>.
            </p>
            {wrongAccount ? (
              <>
                <p className="error">You’re signed in as {signedIn!.email}. Sign in as {info.email} to accept.</p>
                <button
                  className="btn"
                  onClick={() => {
                    tokens.setParent(null);
                    location.reload();
                  }}
                >
                  Sign out
                </button>
              </>
            ) : signedIn ? (
              <button className="btn primary big" onClick={accept} disabled={busy}>
                {busy ? 'Joining…' : `Join ${info.familyName}`}
              </button>
            ) : (
              <>
                <p className="muted">
                  {info.hasAccount ? 'Sign in to accept.' : `Create your account with ${info.email} to accept.`}
                </p>
                {/* A new account joins straight from sign-up, with no family of its own (#507). */}
                <Link to={info.hasAccount ? '/login' : `/signup?invite=${encodeURIComponent(token)}`} className="btn primary big">
                  {info.hasAccount ? 'Sign in' : 'Create account and join'}
                </Link>
                {info.hasAccount && <p className="muted small center">Come back to this link afterwards to finish joining.</p>}
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}
